import { useMemo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { groupNavBySection, isFlagEnabled, type NavEntry } from '@eis/core';
import { usePermissions } from '@eis/patterns';
import { moduleRegistry } from '../app/modules';
import { useChromeVariant } from './useChromeVariant';
import { useDocumentTitle } from './useDocumentTitle';
import { useReturnToSuite } from './useSuite';

interface Crumb {
  readonly label: string;
  readonly to?: string;
  /** The suite root: not a route, the same action as Back (U-5'). */
  readonly toSuite?: boolean;
}

/**
 * The breadcrumb bar under the header — the suite variant's; berry renders none (prompt 24).
 *
 * It is derived from the SAME nav the drawer renders — longest matching `to` prefix wins — rather
 * than from a per-page list. Two consequences worth stating: a detail screen gets a working link
 * back to its list without the module writing one, and the trail can never name an area the
 * signed-in user is not allowed to see, because `visibleNav` filtered it first.
 *
 * It deliberately stops at the area. The shell has no idea that `RV-2025-0001` is called
 * "Bike Comfort Fleet" — the screen does, and it puts that in its own `<h1>`. A shell that guessed
 * would either show a raw id or duplicate the page title one line above it.
 *
 * In the suite variant the trail starts with "EMIL Insurance Suite" instead of "Start" (Board 2 of
 * the hand-off sketches): the user always sees which window is the parent, and the first crumb IS
 * Back — it focuses the legacy tab or navigates to its dashboard. The console's own start page
 * stays reachable from the drawer.
 */
export function AppBreadcrumbs(): ReactNode {
  const { t } = useTranslation(['shell', 'insurance']);
  const permissions = usePermissions();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const suite = useChromeVariant().variant === 'suite';
  const returnToSuite = useReturnToSuite();

  const crumbs = useMemo<readonly Crumb[]>(() => {
    const home: Crumb = suite
      ? { label: t('shell:app.suiteName'), toSuite: true }
      : { label: t('shell:nav.home'), to: pathname === '/' ? undefined : '/' };
    if (pathname === '/') return suite ? [home, { label: t('shell:nav.home') }] : [home];

    const sections = groupNavBySection(
      moduleRegistry.visibleNav(permissions, (flag) => isFlagEnabled(flag, true)),
    );

    let best: { section?: string; entry: NavEntry } | null = null;
    for (const section of sections) {
      for (const entry of section.entries) {
        const matches = pathname === entry.to || pathname.startsWith(`${entry.to}/`);
        if (!matches) continue;
        if (best === null || entry.to.length > best.entry.to.length) {
          best = { section: section.title, entry };
        }
      }
    }
    if (best === null) return [home];

    const areaCrumb: Crumb = {
      // Manifest labels are namespaced keys the shell cannot know statically; see AppNav.
      label: t(best.entry.label as never),
      // The area is a link only when it is not already the page being looked at.
      to: pathname === best.entry.to ? undefined : best.entry.to,
    };
    if (best.section === undefined) return [home, areaCrumb];

    // A section with one entry often names the same thing the entry does — "Gruppen-Policen"
    // above "Gruppen-Policen". Repeating it would tell the reader nothing and would put two
    // identical labels in one trail, so the section crumb is dropped when it adds no word.
    const sectionLabel = t(best.section as never);
    return sectionLabel === areaCrumb.label
      ? [home, areaCrumb]
      : [home, { label: sectionLabel }, areaCrumb];
  }, [pathname, permissions, suite, t]);

  // The area the trail ends on names the tab: "Gruppen-Policen | EMIL Insurance Suite".
  const area = crumbs.length > 1 ? crumbs.at(-1)?.label : undefined;
  useDocumentTitle(area);

  // Berry (EIS Design System, prompt 24 Phase 24.2) has no breadcrumb band: the active nav entry
  // says which area you are in and a detail page carries its own back link
  // (`EntityHeader.backTo`). The trail is still computed, because it names the window.
  if (!suite) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 36,
        px: 3,
        bgcolor: 'surfaces.sunken',
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      <Breadcrumbs
        aria-label={t('shell:breadcrumbs.label')}
        separator={<NavigateNextIcon sx={{ fontSize: '0.875rem' }} />}
        sx={{ '& .MuiBreadcrumbs-separator': { mx: 0.5, color: 'text.secondary' } }}
      >
        {crumbs.map((crumb, index) =>
          crumb.toSuite === true ? (
            <MuiLink
              key={crumb.label}
              component="button"
              type="button"
              variant="caption"
              color="text.secondary"
              onClick={() => {
                returnToSuite();
              }}
              data-testid="breadcrumb-suite-root"
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {crumb.label}
            </MuiLink>
          ) : crumb.to === undefined ? (
            <Typography
              key={crumb.label}
              variant="caption"
              color="text.primary"
              sx={{ fontWeight: 600 }}
              // Only the final crumb is the page itself; a section heading in the middle of the
              // trail is a label, and marking it `current` would announce two current pages.
              aria-current={index === crumbs.length - 1 ? 'page' : undefined}
            >
              {crumb.label}
            </Typography>
          ) : (
            <Typography
              key={crumb.label}
              component={Link}
              to={crumb.to}
              variant="caption"
              color="text.secondary"
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {crumb.label}
            </Typography>
          ),
        )}
      </Breadcrumbs>
    </Box>
  );
}
